
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";

export function useAdminAuth() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  const checkAdminStatus = async (userId: string) => {
    try {
      console.log('Checking admin status for user:', userId);
      
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (rolesError) {
        console.error('Error fetching user roles:', rolesError);
        setError("Error checking permissions");
        setIsLoading(false);
        return;
      }

      console.log('User roles:', roles);
      
      if (roles?.role === 'admin') {
        console.log('User confirmed as admin');
        setIsAdmin(true);
        setError(null);
      } else {
        console.log('User is not an admin');
        setError("You must be an admin to access this page");
        setIsAdmin(false);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error in checkAdminStatus:', error);
      setError("Error checking permissions");
      setIsAdmin(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const setupAuth = async () => {
      try {
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Error getting session:', sessionError);
          setError("Error checking session");
          setIsLoading(false);
          return;
        }

        setSession(currentSession);

        // Set up auth state change listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('Auth state changed:', event, session?.user?.id);
            setSession(session);
            if (session?.user) {
              await checkAdminStatus(session.user.id);
            } else {
              setIsAdmin(false);
              setError("You must be logged in");
              setIsLoading(false);
            }
          }
        );

        // Check initial session
        if (currentSession?.user) {
          await checkAdminStatus(currentSession.user.id);
        } else {
          setIsAdmin(false);
          setError("You must be logged in");
          setIsLoading(false);
        }

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Error in setupAuth:', error);
        setError("Error checking permissions");
        setIsAdmin(false);
        setIsLoading(false);
      }
    };

    setupAuth();
  }, []);

  return { isAdmin, error, isLoading, session };
}
